// ============================================================================
// Instagram DM — capability glue.
//
// Bridges the provider-specific helpers in `send.ts` + `profile.ts` into the
// shape the capability registry expects (`AdapterMethods<'messaging'>`).
//
// Call-site:
//   import { buildInstagramDmMessagingMethods } from './capability-methods'
//   register('instagram-dm', 'messaging', buildInstagramDmMessagingMethods({}))
//
// The `ctx` argument on each method is the `AdapterContext` the dispatcher
// hands in — carrying the decrypted access token + per-instance config JSON.
// ============================================================================

import type { AdapterMethods } from '../../services/capability-registry'
import type { SendMessageArgs } from '../../capabilities/messaging'
import { fetchUserProfile as rawFetchUserProfile } from './profile'
import {
  sendInstagramMessage as rawSendMessage,
  sendInstagramTyping as rawSendTyping,
} from './send'

// ============================================================================
// Dependencies
// ============================================================================

export interface InstagramDmMessagingMethodsDeps {
  /**
   * Injectable fetch for tests. Defaults to the global `fetch`. Keep this as
   * the ONLY moving part so registration in production stays trivial.
   */
  fetchImpl?: typeof fetch
  /**
   * Optional logger surface. Adapters default to `console.*`; wiring a custom
   * logger here routes the advisory `sendTyping` error swallow-logs through
   * the platform observability pipe once it lands.
   */
  logger?: {
    error: (msg: string, extra?: Record<string, unknown>) => void
  }
}

/**
 * Issue #249 M6: replaced the previous
 * `args as typeof args & InstagramSendMessageExtras` type-launder cast with
 * an explicit runtime narrower. The narrower reads the raw JSON-esque args
 * bag without type assertions, so a future rename of any extras field
 * surfaces as a narrower diff (+  fails at tests) instead of silently
 * losing the field at runtime.
 */
interface NarrowedInstagramExtras {
  outside24h: boolean
  messagingType?: 'RESPONSE' | 'UPDATE' | 'MESSAGE_TAG'
  tag?: 'HUMAN_AGENT'
}

/**
 * Pure runtime narrower. Inspects `args.metadata`, `args.messagingType`,
 * `args.tag` without a type cast — every field validated individually so
 * a malformed metadata blob can't crash the send path.
 */
function narrowExtras(args: SendMessageArgs): NarrowedInstagramExtras {
  const raw = args as unknown as Record<string, unknown>

  let outside24h = false
  const meta = raw['metadata']
  if (meta && typeof meta === 'object') {
    const metaObj = meta as Record<string, unknown>
    if (typeof metaObj['outside24h'] === 'boolean') {
      outside24h = metaObj['outside24h'] as boolean
    }
  }

  const mt = raw['messagingType']
  const messagingType =
    mt === 'RESPONSE' || mt === 'UPDATE' || mt === 'MESSAGE_TAG' ? mt : undefined

  const rawTag = raw['tag']
  const tag = rawTag === 'HUMAN_AGENT' ? 'HUMAN_AGENT' : undefined

  return messagingType !== undefined
    ? tag !== undefined
      ? { outside24h, messagingType, tag }
      : { outside24h, messagingType }
    : tag !== undefined
      ? { outside24h, tag }
      : { outside24h }
}

// ============================================================================
// buildInstagramDmMessagingMethods
// ============================================================================

export function buildInstagramDmMessagingMethods(
  deps: InstagramDmMessagingMethodsDeps = {},
): AdapterMethods<'messaging'> {
  const fetchImpl = deps.fetchImpl ?? fetch
  return {
    async sendMessage(args, ctx) {
      // Issue #249 M6: narrow the optional IG-specific hints at RUNTIME instead
      // of type-laundering the args bag. Each field is individually validated.
      const extras = narrowExtras(args)

      return rawSendMessage(
        {
          recipientId: args.to,
          text: args.text,
          attachments: args.attachments,
          isOutside24hWindow: extras.outside24h,
          ...(extras.messagingType ? { messagingType: extras.messagingType } : {}),
          ...(extras.tag ? { tag: extras.tag } : {}),
        },
        ctx.accessToken,
        fetchImpl,
      )
    },
    async sendTyping(args, ctx) {
      // Issue #249 M4: pass tenant context through to the swallowed-error log
      // so operators can grep Workers tail output for a specific instance.
      await rawSendTyping(
        {
          recipientId: args.to,
          state: args.state,
          instanceId: ctx.instanceId,
          companyId: ctx.companyId,
        },
        ctx.accessToken,
        fetchImpl,
      )
    },
    async fetchUserProfile(args, ctx) {
      return rawFetchUserProfile(
        { userId: args.userId },
        ctx.accessToken,
        fetchImpl,
      )
    },
  }
}
